import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { X, Trash2, Link2, Check, Send } from "lucide-react";
import { Button, EmptyState, Sheet, Spinner, Icon, Picker, formatRelativeTime, useModalA11y } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { usePhoneLayout } from "../../app/phoneLayout.js";
import { useMediaDownload } from "../../app/hooks/useMedia.js";
import { useAttachTaskMedia, useRemoveTaskMedia, type TaskMediaDto } from "../../app/hooks/useTaskMedia.js";
import { pushToast } from "../../app/toasts.js";
import { MediaImage } from "../releases/MediaImage.js";
import { PushMediaDialog, type PushSource } from "./PushMediaDialog.js";
import { allowsPush, mediaMeta, typeLabel } from "./taskMedia.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useAssignableRows } from "../../app/hooks/useAssignable.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import {
  useTaskDetail,
  useTransitionTask,
  useAssignTask,
  useUnassignTask,
  useAddTaskComment,
  useDeleteTask,
  useUpdateTask,
  useSetSubtasks,
  advanceActions,
  approvalActions,
  statusLabel,
  formatDueDate,
  formatDropDate,
  isOverdue,
  TASK_TITLE_MAX,
  TASK_NOTES_MAX,
  type TaskDetail,
  type UpdateTaskRequest,
  type TaskSubtask,
  type TaskSubtaskInput,
  type TaskAssigneeDto,
  type TaskCommentDto,
  type TaskActivityDto,
} from "../../app/hooks/useTasks.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { toPersonPickerOption } from "../shared/PersonPickerOption.js";
import { useTaskPeople, type TaskPeople, type TaskPersonRef } from "./taskPeople.js";
import { statusDotTone } from "./taskStatusDot.js";
import styles from "./TaskDrawer.module.scss";

/**
 * The task DRAWER — the routed-modal `?task=:id` surface (that URL is also the `task_assigned` push
 * deep-link target, so it is not decoration). Built to the spec's specimen (`3 · TASK DRAWER (RIGHT-SIDE,
 * 440PX)`) in its order: title · status pills · approval · assignees · due · notes · subtasks · visible-to ·
 * media (uploads + links, ART-368) · comments · activity, under the white header that carries the `TASK` eyebrow, the release the task
 * belongs to, the save marker and the initiator's delete.
 *
 * Everything here is EDITABLE and every write goes through the shared hooks: field commits PUT the whole
 * editable surface with `If-Match`; transitions, assign, unassign and the checklist are optimistic with
 * rollback; a failed comment stays on screen flagged "(not saved)" rather than vanishing. Nothing reports
 * success on an unpersisted write.
 *
 * Chrome is the shared `useModalA11y` (Escape stack · focus trap · focus restore · the ref-counted page
 * lock), never hand-rolled.
 */
export function TaskDrawer({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const q = useTaskDetail(taskId);
  const me = useMe();
  const drawerRef = useRef<HTMLDivElement>(null);
  useModalA11y(drawerRef, onClose);

  const detail = q.data;
  const myPersonId = me.data?.person?.personId;

  return (
    <div className={styles.backdrop}>
      <button type="button" className={styles.scrim} aria-label="Close task" tabIndex={-1} onClick={onClose} />
      <aside
        ref={drawerRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label="Task detail"
        tabIndex={-1}
      >
        {q.isLoading ? (
          <>
            <DrawerHead release={null} onClose={onClose} />
            <div className={styles.center} role="status" aria-live="polite">
              <Spinner size={22} label="Loading task…" />
            </div>
          </>
        ) : q.isError ? (
          <>
            <DrawerHead release={null} onClose={onClose} />
            <div className={styles.body}>
              {q.error instanceof ApiError && q.error.status === 404 ? (
                <EmptyState title="Task not available" description="It was deleted, or you no longer have access." />
              ) : (
                <EmptyState
                  title="Couldn't load this task"
                  description={q.error instanceof Error ? q.error.message : "Please try again."}
                  action={<Button onClick={() => void q.refetch()}>Retry</Button>}
                />
              )}
            </div>
          </>
        ) : detail ? (
          <TaskBody detail={detail} myPersonId={myPersonId} onClose={onClose} />
        ) : null}
      </aside>
    </div>
  );
}

/** The paper header bar: the `TASK` eyebrow over the release this task belongs to, then the save marker and
 *  the icon actions. It renders in EVERY state, so a loading or 404 drawer still has a way out. */
function DrawerHead({
  release,
  saving,
  onDelete,
  onClose,
}: {
  release: string | null;
  saving?: boolean;
  onDelete?: () => void;
  onClose: () => void;
}) {
  return (
    <header className={styles.head}>
      <div className={styles.headMeta}>
        <span className={styles.eyebrow}>Task</span>
        {/* Only when there IS one. The specimen keeps an em-dash placeholder here to hold the header's height,
            but the height only differs BETWEEN tasks — never within one — so nobody sees it move, and a
            standalone task is most tasks. A slot that exists to say "nothing" is not worth a line. */}
        {release && <span className={styles.release}>{release}</span>}
      </div>
      {saving && (
        <span className={styles.saving} role="status">
          Saving…
        </span>
      )}
      {onDelete && (
        <button type="button" className={styles.headIcon} data-danger="true" aria-label="Delete task" onClick={onDelete}>
          <Icon icon={Trash2} size={16} />
        </button>
      )}
      <button type="button" className={styles.headIcon} aria-label="Close" onClick={onClose}>
        <Icon icon={X} size={20} />
      </button>
    </header>
  );
}

/**
 * Focus a field when the form holding it is REVEALED. Not the `autoFocus` prop, which the a11y gate bans and
 * rightly so — that fires on page load, before anyone asked for it. This fires only when someone has just
 * pressed "+ Add", where landing in the field is the whole point of the press.
 */
function useRevealFocus<T extends HTMLElement>(open: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);
  return ref;
}

// ---- body ----------------------------------------------------------------------------------------------

/** The four pills the spec's picker offers, in order. The vocabulary is SEVEN — `blocked`, `approved` and
 *  `revisions_requested` are reachable but not offered here; when the task IS in one of them the badge above
 *  the row says so, which is the legacy's own `showStatusBadge` mechanism. */
const STATUS_PILLS = [
  { status: "open", label: "Open" },
  { status: "in_progress", label: "In progress" },
  { status: "needs_review", label: "Needs review" },
  { status: "done", label: "Done" },
] as const;

function TaskBody({
  detail,
  myPersonId,
  onClose,
}: {
  detail: TaskDetail;
  myPersonId: string | undefined;
  onClose: () => void;
}) {
  const taskId = detail.taskId as string;
  const isInitiator = !!myPersonId && myPersonId === detail.initiatorPersonId;
  const update = useUpdateTask();
  const del = useDeleteTask();
  const [confirmDelete, setConfirmDelete] = useState(false);

  /** Commit one field. The PUT is a full replacement, so send the whole editable surface with the change
   *  folded in — a partial body would clear whatever it omitted. */
  function saveFields(patch: Partial<UpdateTaskRequest>) {
    update.mutate({ taskId, version: detail.version ?? 0, body: { ...editableBody(detail), ...patch } });
  }

  return (
    <>
      <DrawerHead
        release={releaseLine(detail)}
        saving={update.isPending}
        onDelete={isInitiator ? () => setConfirmDelete(true) : undefined}
        onClose={onClose}
      />

      {confirmDelete && (
        <div className={styles.deleteConfirm} role="alertdialog" aria-label="Confirm delete">
          <span className={styles.deleteConfirmText}>Delete this task? This can&apos;t be undone.</span>
          <button type="button" className={styles.dcCancel} onClick={() => setConfirmDelete(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.dcConfirm}
            disabled={del.isPending}
            onClick={() => del.mutate(taskId, { onSuccess: onClose })}
          >
            Yes, delete
          </button>
        </div>
      )}

      <div className={styles.body}>
        <CompletionBanner detail={detail} />
        <TitleField detail={detail} onSave={(title) => saveFields({ title })} />
        <StatusRow detail={detail} isInitiator={isInitiator} />
        <ApprovalPanel detail={detail} isInitiator={isInitiator} />
        <AssigneesSection detail={detail} myPersonId={myPersonId} />
        <DueSection detail={detail} onSave={saveFields} />
        <NotesField detail={detail} onSave={(description) => saveFields({ description })} />
        <SubtasksSection detail={detail} />
        <MediaSection detail={detail} myPersonId={myPersonId} isInitiator={isInitiator} onSave={saveFields} />
        <CommentThread detail={detail} />
        <ActivityFeed activity={detail.activity ?? []} directory={detail.people ?? undefined} />
      </div>
    </>
  );
}

/**
 * What the header says about the release: its name and drop date, or — when the task is release-scoped but
 * the release does not resolve (cancelled, soft-deleted) — the bare fact that it IS a rollout task. That
 * fallback is the point: without it a task on a cancelled release renders exactly like a standalone one,
 * which is misinformation rather than merely a gap. Null for a genuinely standalone task, and the header
 * then drops the line entirely.
 */
function releaseLine(detail: TaskDetail): string | null {
  if (!detail.releaseId) return null;
  if (!detail.releaseTitle) return "Release task";
  const drops = formatDropDate(detail.releaseDate);
  return drops ? `${detail.releaseTitle} · Drops ${drops}` : detail.releaseTitle;
}

/** A section's head: the mono label on the left, its one action on the right. */
function SectionHead({ label, extra, action }: { label: string; extra?: string; action?: ReactNode }) {
  return (
    <div className={styles.sectionHead}>
      <p className={styles.sectionLabel}>
        {label}
        {extra && <span className={styles.sectionExtra}>{extra}</span>}
      </p>
      {action}
    </div>
  );
}

function SectionAction({
  label,
  onClick,
  keepFocus,
}: {
  label: string;
  onClick: () => void;
  /** Don't take focus off whatever is being typed into. Pressing this while a draft row is open must be able
   *  to put the caret BACK in it — and a plain button would blur it (committing the row) before the click
   *  handler ever runs. */
  keepFocus?: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.sectionAction}
      onMouseDown={keepFocus ? (e) => e.preventDefault() : undefined}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// ---- status --------------------------------------------------------------------------------------------

/**
 * The status picker. Every pill is drawn, but only the LEGAL next moves are pressable: the server owns the
 * transition graph and answers 422 to anything else, so a pill that would be refused is disabled and says why
 * rather than inviting a click that fails. The legacy let you set any status freely — this is the same
 * picker with the workflow it was always supposed to have.
 */
function StatusRow({ detail, isInitiator }: { detail: TaskDetail; isInitiator: boolean }) {
  const taskId = detail.taskId as string;
  const transition = useTransitionTask();
  const status = detail.status ?? "open";

  const legal = new Set(
    [...advanceActions(status), ...(isInitiator ? approvalActions(status) : [])].map((a) => a.toStatus as string),
  );
  const offered = new Set<string>(STATUS_PILLS.map((p) => p.status));

  return (
    <>
      {/* The current status has no pill of its own (blocked / approved / revisions requested) — say so, or
          the row shows nothing active and reads as "Open". */}
      {!offered.has(status) && (
        <p className={styles.statusBadge} data-tone={statusDotTone(status)}>
          <span className={styles.dot} data-tone={statusDotTone(status)} aria-hidden="true" />
          {statusLabel(status)}
        </p>
      )}
      <div className={styles.statusRow} role="group" aria-label="Status">
        {STATUS_PILLS.map((pill) => {
          const active = pill.status === status;
          const can = legal.has(pill.status);
          return (
            <button
              key={pill.status}
              type="button"
              className={styles.statusPill}
              data-active={active || undefined}
              aria-pressed={active}
              disabled={!active && !can}
              title={
                !active && !can ? `A task can't move straight from ${statusLabel(status)} to ${pill.label}.` : undefined
              }
              onClick={() => {
                if (active || !can) return;
                transition.mutate({ taskId, toStatus: pill.status, version: detail.version ?? 0, notes: null });
              }}
            >
              <span className={styles.dot} data-tone={statusDotTone(pill.status)} aria-hidden="true" />
              {pill.label}
            </button>
          );
        })}
      </div>
    </>
  );
}


// ---- approval ------------------------------------------------------------------------------------------

/**
 * The sign-off panel, at `needs_review` only. The INITIATOR gets the two decisions; everyone else is told
 * whose desk it is on — the spec shows the panel to both, and one that simply vanished would leave an
 * assignee wondering whether their submission registered.
 *
 * "Request revisions" opens a note field rather than firing immediately: the note is the whole point (the
 * server posts it into the comment thread so the assignee can read WHY), and the legacy collected it through
 * a `window.prompt`.
 */
function ApprovalPanel({ detail, isInitiator }: { detail: TaskDetail; isInitiator: boolean }) {
  const taskId = detail.taskId as string;
  const transition = useTransitionTask();
  const people = useTaskPeople(detail.people ?? undefined);
  const [notes, setNotes] = useState("");
  const [askNotes, setAskNotes] = useState(false);
  const notesRef = useRevealFocus<HTMLTextAreaElement>(askNotes);

  if (detail.status !== "needs_review") return null;
  const initiator = people.nameOf(detail.initiatorPersonId, "the task's creator");

  function decide(toStatus: string, withNotes?: string) {
    transition.mutate({ taskId, toStatus, version: detail.version ?? 0, notes: withNotes ?? null });
  }

  return (
    <section className={styles.approval} aria-label="Review">
      <p className={styles.approvalTitle}>Review requested</p>
      <p className={styles.approvalBody}>
        {isInitiator
          ? "The assignee flagged this ready. Approve it, or send it back with what still needs doing."
          : `The assignee flagged this ready. Only ${initiator} can approve it or send it back.`}
      </p>
      {isInitiator &&
        (askNotes ? (
          <form
            className={styles.approvalNotes}
            onSubmit={(e) => {
              e.preventDefault();
              decide("revisions_requested", notes.trim() || undefined);
              setAskNotes(false);
              setNotes("");
            }}
          >
            <textarea
              ref={notesRef}
              className={styles.textarea}
              rows={2}
              placeholder="What still needs doing?"
              aria-label="Revision notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div className={styles.approvalActions}>
              <Button type="submit" size="sm" loading={transition.isPending}>
                Send it back
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setAskNotes(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className={styles.approvalActions}>
            <Button
              size="sm"
              loading={transition.isPending && transition.variables?.toStatus === "approved"}
              onClick={() => decide("approved")}
            >
              Approve
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAskNotes(true)}>
              Request revisions
            </Button>
          </div>
        ))}
    </section>
  );
}

// ---- completion ----------------------------------------------------------------------------------------

/**
 * The banner a finished task wears: who finished it, when, and how much conversation is on file. Done is not
 * gone — a completed task keeps its whole record, and this says so instead of the drawer looking identical to
 * an open one.
 *
 * The spec pairs this with a "Save permanently" pin against auto-cleanup. Deferred deliberately (operator
 * ruling, 2026-08-23): there is no auto-cleanup sweep in this app, so the pin would guard nothing.
 */
function CompletionBanner({ detail }: { detail: TaskDetail }) {
  const people = useTaskPeople(detail.people ?? undefined);
  const approved = detail.status === "approved";
  if (detail.status !== "done" && !approved) return null;

  const who = detail.completedByPersonId ? people.nameOf(detail.completedByPersonId, "someone") : null;
  const when = formatRelativeTime(detail.completedOn);
  const comments = detail.comments?.length ?? 0;

  return (
    <div className={styles.doneBanner} role="status">
      <span className={styles.doneIcon} aria-hidden="true">
        <Icon icon={Check} size={16} />
      </span>
      <p className={styles.doneText}>
        <strong>{approved ? "Approved" : "Completed"}</strong>
        {who && ` by ${who}`}
        {when && ` · ${when}`}
        {comments > 0 && ` · ${comments} comment${comments === 1 ? "" : "s"} on file`}
      </p>
    </div>
  );
}

// ---- editable fields -----------------------------------------------------------------------------------

/** The current editable surface as a PUT body — the base every field commit folds its change into. */
function editableBody(detail: TaskDetail): UpdateTaskRequest {
  return {
    title: detail.title ?? "",
    description: detail.description ?? null,
    dueDate: detail.dueDate ?? null,
    links: detail.links ?? [],
  };
}

/**
 * The task's title, editable in place. Local state while typing, committed on blur or Enter; a blank title is
 * refused (the server 422s it too) by snapping back to the stored value rather than sending an empty PUT.
 * Re-seeds whenever the STORED title changes, so a save landing — or another editor's — is picked up instead
 * of being masked by our stale draft.
 */
function TitleField({ detail, onSave }: { detail: TaskDetail; onSave: (title: string) => void }) {
  const stored = detail.title ?? "";
  const [value, setValue] = useState(stored);
  useEffect(() => setValue(stored), [stored]);

  function commit() {
    const next = value.trim();
    if (!next || next === stored) {
      setValue(stored);
      return;
    }
    onSave(next);
  }

  return (
    <input
      className={styles.titleInput}
      aria-label="Task title"
      placeholder="Untitled task"
      maxLength={TASK_TITLE_MAX}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    />
  );
}

function DueSection({
  detail,
  onSave,
}: {
  detail: TaskDetail;
  onSave: (patch: Partial<UpdateTaskRequest>) => void;
}) {
  const overdue = isOverdue(detail.dueDate, detail.status);
  return (
    <section className={styles.section}>
      <SectionHead label="Due" />
      <div className={styles.dueRow}>
        <input
          type="date"
          className={styles.dateInput}
          aria-label="Due"
          value={detail.dueDate ?? ""}
          // Emptying the input CLEARS the due date — expressible only because the PUT is a full replacement.
          onChange={(e) => onSave({ dueDate: e.target.value || null })}
        />
        {overdue && <span className={styles.overdueFlag}>Overdue · {formatDueDate(detail.dueDate)}</span>}
      </div>
    </section>
  );
}

/** Notes, commit-on-blur. Blank commits as a CLEAR (the server folds blank → null). */
function NotesField({ detail, onSave }: { detail: TaskDetail; onSave: (notes: string | null) => void }) {
  const stored = detail.description ?? "";
  const [value, setValue] = useState(stored);
  useEffect(() => setValue(stored), [stored]);

  return (
    <section className={styles.section}>
      <SectionHead label="Notes" />
      <textarea
        className={styles.notes}
        aria-label="Notes"
        rows={3}
        placeholder="Anything the assignee needs to know…"
        maxLength={TASK_NOTES_MAX}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => value !== stored && onSave(value.trim() || null)}
      />
    </section>
  );
}

// ---- assignees -----------------------------------------------------------------------------------------

/**
 * Multi-assignee, add and remove. NOT gated on `band:manage`: `POST /assign` and its DELETE twin take
 * `band:read` + the server's reach check, and the list this picker reads is `band:read` too — gating the
 * control tighter than the endpoint left a tour manager staring at a task they could not staff.
 *
 * ⚠️ THE LIST IS `tasks/assignable`, THE GATE READ FORWARDS — not the band's seat list. It listed `GET /team`
 * until ART-121, so from a task that already existed you could only reach BAND-side people: an org staffer
 * the save would plainly have accepted was never offered, because an org seat is not a band seat. The create
 * modal had moved to this source on 2026-09-04 and the drawer had not, so the same band offered two
 * different sets of people depending on whether the task was five seconds or five days old.
 *
 * There is deliberately no "Assign to me" button beside it (removed with the same ticket): self is always in
 * this list — `core.assignable_people` includes the caller unconditionally — so the button was a second,
 * narrower door onto a thing the picker already does, and the only one an OWNER had.
 */
function AssigneesSection({ detail, myPersonId }: { detail: TaskDetail; myPersonId: string | undefined }) {
  const taskId = detail.taskId as string;
  const assign = useAssignTask();
  const unassign = useUnassignTask();
  const candidates = useAssignableRows();
  const people = useTaskPeople(detail.people ?? undefined);
  const [adding, setAdding] = useState(false);

  const assignees = detail.assignees ?? [];
  const assigned = new Set(assignees.map((a) => a.personId).filter(Boolean));
  // The OWNER seat is not a person to hand work to — "the artist IS the band", so its row reads as the band's
  // own name — but it is still yours to assign if it is YOU, which is the create modal's rule exactly.
  const addable = candidates.rows
    .filter((c) => !assigned.has(c.personId) && (!c.isOwner || c.personId === myPersonId))
    .map(toPersonPickerOption);

  const nameOf = (a: TaskAssigneeDto) => people.nameOf(a.personId, "Assignee");

  return (
    <section className={styles.section}>
      <SectionHead
        label="Assignees"
        action={
          addable.length > 0 ? (
            <SectionAction label="+ Add" keepFocus onClick={() => setAdding(true)} />
          ) : undefined
        }
      />
      {adding && addable.length > 0 && (
        <div className={styles.pickerRow}>
          <Picker
            label="Assign a teammate"
            bare
            typeahead
            focusOnMount
            value=""
            options={addable}
            onChange={(personId) => {
              if (!personId) return;
              assign.mutate({ taskId, body: { personId } });
              setAdding(false);
            }}
          />
        </div>
      )}
      {assignees.length === 0 ? (
        <p className={styles.sectionEmpty}>No one assigned yet.</p>
      ) : (
        <div className={styles.chipRow}>
          {assignees.map((a, i) => (
            <span key={`${a.personId ?? "a"}-${i}`} className={styles.assigneeChip}>
              <PersonAvatar
                personId={a.personId}
                avatarMediaId={people.person(a.personId)?.avatarMediaId}
                initials={people.initialsOf(a.personId, nameOf(a))}
                size="sm"
              />
              <span className={styles.assigneeName}>{nameOf(a)}</span>
              {a.personId && (
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`Remove ${nameOf(a)}`}
                  disabled={unassign.isPending && unassign.variables?.personId === a.personId}
                  onClick={() => unassign.mutate({ taskId, personId: a.personId as string })}
                >
                  <Icon icon={X} size={16} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

// ---- subtasks ------------------------------------------------------------------------------------------

/**
 * The checklist. Every mutation — tick, add, delete — sends the WHOLE list, because that is what it is: an
 * ordered list the drawer already holds. The server derives the task's status from it (all done ⇒ done;
 * un-tick one ⇒ back to in progress), which is why the progress count sits in the head and the pills above
 * may move under you after a tick.
 */
function SubtasksSection({ detail }: { detail: TaskDetail }) {
  const taskId = detail.taskId as string;
  const setSubtasks = useSetSubtasks();
  // `null` = no draft row. A string (even "") = a placeholder row is on the list, being typed into.
  const [draft, setDraft] = useState<string | null>(null);
  const draftRef = useRevealFocus<HTMLInputElement>(draft !== null);
  // Escape has to cancel WITHOUT the blur it causes committing the row on the way out.
  const cancelling = useRef(false);
  const rows = detail.subtasks ?? [];
  const done = rows.filter((r) => r.done).length;

  function save(items: TaskSubtask[]) {
    setSubtasks.mutate({
      taskId,
      version: detail.version ?? 0,
      items: items.map((r): TaskSubtaskInput => ({ id: r.id ?? null, title: r.title ?? "", done: r.done ?? false })),
    });
  }

  /** "+ Add" opens the placeholder row — or, if one is already open, just puts the caret back in it. */
  function startDraft() {
    if (draft !== null) {
      draftRef.current?.focus();
      return;
    }
    setDraft("");
  }

  /** Take the value off the ELEMENT rather than state: Enter commits by blurring, and a stale closure would
   *  otherwise let the same text land twice. Empty in ⇒ the row simply goes away. No id either — identity is
   *  the server's to mint, so the client can never invent one that collides. */
  function finishDraft(value: string) {
    const title = value.trim();
    setDraft(null);
    if (title) save([...rows, { id: null, title, done: false }]);
  }

  return (
    <section className={styles.section}>
      <SectionHead
        label="Subtasks"
        extra={rows.length > 0 ? `${done}/${rows.length}` : undefined}
        action={<SectionAction label="+ Add" keepFocus onClick={startDraft} />}
      />
      {rows.length === 0 && draft === null ? (
        <p className={styles.sectionEmpty}>No subtasks yet.</p>
      ) : (
        <ul className={styles.subtaskList}>
          {rows.map((row, i) => (
            <li key={row.id ?? `new-${i}`} className={styles.subtask} data-done={row.done || undefined}>
              <label className={styles.subtaskCheck}>
                <input
                  type="checkbox"
                  className={styles.srOnly}
                  checked={!!row.done}
                  onChange={() => save(rows.map((r, j) => (j === i ? { ...r, done: !r.done } : r)))}
                />
                <span className={styles.subtaskBox} aria-hidden="true">
                  <Icon icon={Check} size={16} />
                </span>
                <span className={styles.subtaskTitle}>{row.title}</span>
              </label>
              <button
                type="button"
                className={styles.rowRemove}
                aria-label={`Delete subtask ${row.title}`}
                onClick={() => save(rows.filter((_, j) => j !== i))}
              >
                <Icon icon={X} size={16} />
              </button>
            </li>
          ))}
          {/* The placeholder row — a subtask that does not exist yet, sitting where it will live, edited in
              place. Enter commits by blurring (one commit path, no double-add); Escape throws it away. */}
          {draft !== null && (
            <li className={styles.subtask} data-draft="true">
              <span className={styles.subtaskBox} aria-hidden="true" />
              <input
                ref={draftRef}
                className={styles.subtaskInput}
                placeholder="Subtask…"
                aria-label="New subtask"
                maxLength={300}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelling.current = true;
                    e.currentTarget.blur();
                  }
                }}
                onBlur={(e) => {
                  if (cancelling.current) {
                    cancelling.current = false;
                    setDraft(null);
                    return;
                  }
                  finishDraft(e.currentTarget.value);
                }}
              />
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

// ---- media (ART-368: Links became Media — uploads and links, and the push to the release) ---------------

function MediaSection({
  detail,
  myPersonId,
  isInitiator,
  onSave,
}: {
  detail: TaskDetail;
  myPersonId: string | undefined;
  isInitiator: boolean;
  onSave: (patch: Partial<UpdateTaskRequest>) => void;
}) {
  const taskId = detail.taskId as string;
  const links = detail.links ?? [];
  const media = detail.media ?? [];
  const phone = usePhoneLayout();
  const canWriteReleases = useCapability("releases:write");
  const isAdmin = useCapability("band:manage");
  const people = useTaskPeople(detail.people ?? undefined);
  const attach = useAttachTaskMedia();
  const remove = useRemoveTaskMedia();
  const fileRef = useRef<HTMLInputElement>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pushing, setPushing] = useState<PushSource | null>(null);
  const [lastPush, setLastPush] = useState<string | null>(null);

  // `null` = no draft row. An object = a placeholder row is on the list, being typed into.
  const [draft, setDraft] = useState<{ label: string; url: string } | null>(null);
  const urlRef = useRevealFocus<HTMLInputElement>(draft !== null);
  const cancelling = useRef(false);

  const releaseTitle = detail.releaseTitle ?? null;
  // A push needs a finished task on a release, and Releases access. Moving the task back out of Done hides it.
  const canPush = Boolean(detail.releaseId && releaseTitle) && allowsPush(detail.status) && canWriteReleases;
  const mayRemove = (createdBy: string | null | undefined) => isAdmin || isInitiator || (!!myPersonId && createdBy === myPersonId);

  /** "+ Link" opens the placeholder row — or, if one is open, puts the caret back in its URL. */
  function startDraft() {
    if (draft !== null) {
      urlRef.current?.focus();
      return;
    }
    setDraft({ label: "", url: "" });
  }

  /** A link with no URL is not a link: an empty draft simply goes away. The label is optional and falls back
   *  to the host, which is what the row would show anyway. */
  function finishDraft(next: { label: string; url: string }) {
    const raw = next.url.trim();
    setDraft(null);
    if (!raw) return;
    const href = normalizeUrl(raw);
    onSave({ links: [...links, { url: href, label: next.label.trim() || hostOf(href) }] });
  }

  /** Enter commits by blurring the row (one commit path); with no URL yet it moves to the URL instead of
   *  committing nothing. Escape throws the draft away. */
  function onDraftKey(e: KeyboardEvent<HTMLInputElement>, current: { label: string; url: string }) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!current.url.trim()) {
        urlRef.current?.focus();
        return;
      }
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelling.current = true;
      e.currentTarget.blur();
    }
  }

  function onFiles(list: FileList | null) {
    const files = Array.from(list ?? []);   // snapshot BEFORE the reset below — the list is live (the Dropzone rule)
    if (fileRef.current) fileRef.current.value = "";
    if (files.length > 0) attach.mutate({ taskId, files });
  }

  const empty = media.length === 0 && links.length === 0 && draft === null;
  const doneBy = detail.completedByPersonId ? people.nameOf(detail.completedByPersonId, "someone") : null;

  return (
    <section className={styles.section}>
      <SectionHead
        label="Media"
        action={
          phone ? (
            <SectionAction label="+ Add" onClick={() => setAddOpen(true)} />
          ) : (
            <span className={styles.sectionActions}>
              <SectionAction label="↑ Upload" onClick={() => fileRef.current?.click()} />
              <SectionAction label="+ Link" keepFocus onClick={startDraft} />
            </span>
          )
        }
      />
      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        aria-label="Upload files"
        onChange={(e) => onFiles(e.target.files)}
      />
      {attach.isPending && (
        <p className={styles.mediaStatus} role="status">
          Uploading…
        </p>
      )}

      {empty ? (
        <p className={styles.sectionEmpty}>No media yet.</p>
      ) : (
        <>
          {media.length > 0 && (
            <ul className={styles.mediaGrid}>
              {media.map((m) => (
                <MediaTile
                  key={m.taskMediaId}
                  item={m}
                  compact={phone}
                  canPush={canPush}
                  canRemove={mayRemove(m.createdByPersonId)}
                  removing={remove.isPending && remove.variables?.taskMediaId === m.taskMediaId}
                  onPush={() => setPushing({ kind: "upload", media: m })}
                  onRemove={() => remove.mutate({ taskId, taskMediaId: m.taskMediaId as string })}
                />
              ))}
            </ul>
          )}

          {(links.length > 0 || draft !== null) && (
            <ul className={styles.linkList}>
              {links.map((l, i) => (
                <li key={`${l.url}-${i}`} className={styles.linkRow}>
                  <Icon icon={Link2} size={16} className={styles.linkIcon} />
                  <a className={styles.linkLabel} href={l.url ?? "#"} target="_blank" rel="noreferrer noopener">
                    {l.label || l.url}
                  </a>
                  <span className={styles.linkHost}>{hostOf(l.url ?? "")}</span>
                  {canPush && (
                    <button
                      type="button"
                      className={styles.pushPill}
                      data-compact={phone || undefined}
                      onClick={() => setPushing({ kind: "link", url: l.url ?? "", label: l.label || hostOf(l.url ?? "") })}
                    >
                      {phone ? "Push" : "Push to release"}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.rowRemove}
                    aria-label={`Remove link ${l.label || l.url}`}
                    onClick={() => onSave({ links: links.filter((_, j) => j !== i) })}
                  >
                    <Icon icon={X} size={16} />
                  </button>
                </li>
              ))}
              {/* The placeholder row, in the slots the committed row uses: an optional name on the left, the URL
                  where the host will end up. Blur is watched on the ROW so tabbing between the fields doesn't commit. */}
              {draft !== null && (
                <li
                  className={styles.linkRow}
                  data-draft="true"
                  onBlur={(e) => {
                    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                    if (cancelling.current) {
                      cancelling.current = false;
                      setDraft(null);
                      return;
                    }
                    finishDraft(draft);
                  }}
                >
                  <Icon icon={Link2} size={16} className={styles.linkIcon} />
                  <input
                    className={styles.linkDraftLabel}
                    placeholder="Label (optional)"
                    aria-label="Link label"
                    value={draft.label}
                    onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                    onKeyDown={(e) => onDraftKey(e, draft)}
                  />
                  <input
                    ref={urlRef}
                    className={styles.linkDraftUrl}
                    placeholder="Paste a URL"
                    aria-label="Link URL"
                    value={draft.url}
                    onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                    onKeyDown={(e) => onDraftKey(e, draft)}
                  />
                </li>
              )}
            </ul>
          )}
        </>
      )}

      {canPush && (media.length > 0 || links.length > 0) && (
        <p className={styles.pushNote}>
          {doneBy ? `Marked Done by ${doneBy} · ` : ""}pick a piece to push to {releaseTitle}
        </p>
      )}
      {lastPush && (
        <p className={styles.pushLog} role="status">
          {lastPush} · just now
        </p>
      )}

      {addOpen && (
        <Sheet title="Add media" onClose={() => setAddOpen(false)} stacked>
          <div className={styles.addSheet}>
            <Button
              variant="ghost"
              fullWidth
              onClick={() => {
                setAddOpen(false);
                fileRef.current?.click();
              }}
            >
              Photo or file
            </Button>
            <Button
              variant="ghost"
              fullWidth
              onClick={() => {
                setAddOpen(false);
                startDraft();
              }}
            >
              Add link
            </Button>
          </div>
        </Sheet>
      )}

      {pushing && detail.releaseId && releaseTitle && (
        <PushMediaDialog
          taskId={taskId}
          releaseId={detail.releaseId}
          releaseTitle={releaseTitle}
          source={pushing}
          onClose={() => setPushing(null)}
          onPushed={(result, slotLabel) => {
            const name = pushing.kind === "upload" ? pushing.media.fileName || "File" : pushing.label;
            setPushing(null);
            setLastPush(`${name} → ${slotLabel}`);
            pushToast(`Pushed to ${slotLabel} · ${releaseTitle} · ${result.assetsAdded} / ${result.assetsTotal} in`, "success");
          }}
        />
      )}
    </section>
  );
}

/** One upload: its thumbnail (or a type tile), name and meta, and — on a finished release task — Push. Tapping
 *  the thumbnail downloads the original. */
function MediaTile({
  item,
  compact,
  canPush,
  canRemove,
  removing,
  onPush,
  onRemove,
}: {
  item: TaskMediaDto;
  compact: boolean;
  canPush: boolean;
  canRemove: boolean;
  removing: boolean;
  onPush: () => void;
  onRemove: () => void;
}) {
  const { download } = useMediaDownload();
  const name = item.fileName || "File";
  const meta = mediaMeta(item);
  const isImage = item.kind === "image";

  return (
    <li className={styles.mediaTile}>
      <button
        type="button"
        className={styles.mediaThumb}
        aria-label={`Open ${name}`}
        onClick={() => download(item.mediaId as string, name).catch((e) => pushToast(errorMessage(e, "Couldn't open the file")))}
      >
        {isImage ? (
          <MediaImage mediaId={item.mediaId} alt="" className={styles.mediaImg} />
        ) : (
          <span className={styles.mediaType}>{typeLabel(item.fileName, item.kind)}</span>
        )}
      </button>
      {canRemove && (
        <button
          type="button"
          className={styles.mediaRemove}
          aria-label={`Remove ${name}`}
          disabled={removing}
          onClick={onRemove}
        >
          <Icon icon={X} size={16} />
        </button>
      )}
      <span className={styles.mediaName}>{name}</span>
      {meta && <span className={styles.mediaMeta}>{meta}</span>}
      {canPush && (
        <button type="button" className={styles.pushPill} data-block data-compact={compact || undefined} onClick={onPush}>
          {compact ? "Push" : "Push to release"}
        </button>
      )}
    </li>
  );
}

/** A pasted bare domain is a URL with the scheme left off — make it one rather than shipping a dead href. */
function normalizeUrl(raw: string): string {
  // eslint-disable-next-line no-restricted-syntax -- the SCHEME for a user's pasted link, not an API/WS base
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

/** The host, as the default chip label ("open.spotify.com"). Falls back to the raw string for an unparseable
 *  URL — a link nobody can read still beats swallowing what someone typed. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

// ---- comments ------------------------------------------------------------------------------------------

/** A comment that failed to post, kept on screen rather than thrown away. */
interface FailedComment {
  readonly key: string;
  readonly body: string;
  readonly error: string;
}

/**
 * The thread. A failed post is NOT swallowed and NOT silently retried: the bubble stays, flagged
 * "(not saved)" with the server's own words plus a Retry, so nobody loses what they typed and nobody is told
 * it landed when it didn't (the spec's rule, and the no-phantom-success rule either way).
 */
function CommentThread({ detail }: { detail: TaskDetail }) {
  const taskId = detail.taskId as string;
  const add = useAddTaskComment();
  const people = useTaskPeople(detail.people ?? undefined);
  const [text, setText] = useState("");
  const [failed, setFailed] = useState<FailedComment[]>([]);
  const [seq, setSeq] = useState(0);
  const comments = detail.comments ?? [];

  function post(body: string, retryKey?: string) {
    add.mutate(
      { taskId, body },
      {
        onSuccess: () => {
          setText("");
          if (retryKey) setFailed((f) => f.filter((x) => x.key !== retryKey));
        },
        onError: (err) => {
          const message = err instanceof Error && err.message ? err.message : "Save failed";
          setText("");
          setSeq((n) => n + 1);
          setFailed((f) => [...f.filter((x) => x.key !== retryKey), { key: retryKey ?? `f${seq}`, body, error: message }]);
        },
      },
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const body = text.trim();
    if (body) post(body);
  }

  return (
    <section className={styles.section}>
      <SectionHead label="Comments" />
      {comments.length === 0 && failed.length === 0 ? (
        <p className={styles.sectionEmpty}>No comments yet.</p>
      ) : (
        <ul className={styles.commentList}>
          {comments.map((c: TaskCommentDto) => (
            <li key={c.commentId} className={styles.comment}>
              <div className={styles.commentHead}>
                <PersonAvatar
                  personId={c.authorPersonId}
                  avatarMediaId={people.person(c.authorPersonId)?.avatarMediaId}
                  initials={people.initialsOf(c.authorPersonId, people.nameOf(c.authorPersonId, "Someone"))}
                  size="sm"
                />
                <span className={styles.commentAuthor}>{people.nameOf(c.authorPersonId, "Someone")}</span>
                <span className={styles.commentTime}>{formatRelativeTime(c.createdOn)}</span>
              </div>
              <p className={styles.commentBody}>{c.body}</p>
            </li>
          ))}
          {failed.map((f) => (
            <li key={f.key} className={styles.comment} data-failed="true">
              <div className={styles.commentHead}>
                <span className={styles.commentAuthor}>You (not saved)</span>
                <button type="button" className={styles.commentRetry} onClick={() => post(f.body, f.key)}>
                  Retry
                </button>
                <button
                  type="button"
                  className={styles.rowRemove}
                  aria-label="Discard unsaved comment"
                  onClick={() => setFailed((all) => all.filter((x) => x.key !== f.key))}
                >
                  <Icon icon={X} size={16} />
                </button>
              </div>
              <p className={styles.commentBody}>{f.body}</p>
              <p className={styles.commentError}>{f.error}</p>
            </li>
          ))}
        </ul>
      )}
      <form className={styles.commentForm} onSubmit={onSubmit}>
        <textarea
          className={styles.commentInput}
          rows={2}
          placeholder="Add a comment…"
          aria-label="Add a comment"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" size="sm" loading={add.isPending} disabled={!text.trim()} className={styles.commentSend}>
          <Icon icon={Send} size={16} /> Post
        </Button>
      </form>
    </section>
  );
}

// ---- activity ------------------------------------------------------------------------------------------

function ActivityFeed({ activity, directory }: { activity: TaskActivityDto[]; directory: TaskPersonRef[] | undefined }) {
  // The only people-rendering section that is not handed the whole `detail`, so the directory arrives as its
  // own prop. An activity ACTOR is exactly the org staffer the seat list cannot name (ART-118) — this feed
  // is where "Ryan assigned Casey" is written down.
  const people = useTaskPeople(directory);
  if (activity.length === 0) return null;
  // Newest first — the legacy reverses the append-only log for display, and the most recent move is what you
  // opened the drawer to check.
  const rows = [...activity].reverse();
  return (
    <section className={styles.section}>
      <SectionHead label="Activity" />
      <ul className={styles.activityList}>
        {rows.map((a) => (
          <li key={a.activityId} className={styles.activityRow}>
            <span className={styles.activityText}>{humanizeActivity(a, people)}</span>
            <span className={styles.activityTime}>{formatRelativeTime(a.createdOn)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---- helpers -------------------------------------------------------------------------------------------

// The verbs the SERVICE actually writes (`ITasksService`: created · updated · deleted · assigned ·
// unassigned · commented · `status:{to}`). They are read as a sentence after the actor's name, so they start
// lowercase and stay in the past tense. A status change arrives as `status:needs_review` — a PREFIXED action,
// not a key — which is why this can't be a plain lookup table.
const ACTIVITY_VERBS: Record<string, string> = {
  created: "created this task",
  updated: "updated the details",
  deleted: "deleted this task",
  commented: "commented",
};

const STATUS_PREFIX = "status:";

/** "Casey Rivera changed status to Needs review" — actor + verb, resolved off the band's seat list. */
function humanizeActivity(a: TaskActivityDto, people: TaskPeople): string {
  const action = a.action ?? "";
  const actor = people.nameOf(a.actorPersonId, "Someone");

  if (action.startsWith(STATUS_PREFIX))
    return `${actor} changed status to ${statusLabel(action.slice(STATUS_PREFIX.length))}`;

  if (action === "assigned" || action === "unassigned") {
    // The payload is `{personId}` — jsonb, so `unknown` on the wire until it's narrowed.
    const data = a.data as { personId?: string | null } | null | undefined;
    const target = data?.personId;
    if (action === "unassigned") return `${actor} unassigned ${people.nameOf(target, "someone")}`;
    return target === a.actorPersonId ? `${actor} took this on` : `${actor} assigned ${people.nameOf(target, "someone")}`;
  }

  const verb = ACTIVITY_VERBS[action] ?? (action.replace(/_/g, " ") || "made a change");
  return `${actor} ${verb}`;
}
